import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Email as EmailIcon,
  Add as AddIcon,
  Reply as ReplyIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useDatabase } from '../../hooks/useDatabase';
import { useGmail } from '../../hooks/useGmail';
import { Event } from '../../types/database';
import { EmailMetadata } from '../../types/gmail';

interface ContactEmailsProps {
  contactId: string;
  contactEmail?: string;
  contactName?: string;
  onComposeEmail?: (to: string) => void;
}

export function ContactEmails({ 
  contactId, 
  contactEmail, 
  contactName,
  onComposeEmail 
}: ContactEmailsProps) {
  const db = useDatabase();
  const { error: gmailError, listEmails, checkConnection, getEmail } = useGmail();
  
  const [emailEvents, setEmailEvents] = useState<Event[]>([]);
  const [gmailEmails, setGmailEmails] = useState<EmailMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Event | EmailMetadata | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [fullEmailContent, setFullEmailContent] = useState<string | null>(null);
  const [loadingEmailContent, setLoadingEmailContent] = useState(false);

  const loadGmailEmails = useCallback(async () => {
    if (!contactEmail || !isGmailConnected) return;

    try {
      // Search for emails from or to this contact in inbox and sent folders
      const query = `(from:${contactEmail} OR to:${contactEmail}) (in:inbox OR in:sent)`;
      const emailFilters = {
        maxResults: 50,
        query: query
      };

      const result = await listEmails(emailFilters);
      setGmailEmails(result.emails);
    } catch (err) {
      console.error('Failed to load Gmail emails:', err);
    }
  }, [contactEmail, isGmailConnected, listEmails]);

  const loadEmailEvents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: events, error: eventsError } = await db.getEventsByContact(contactId);
      
      if (eventsError) {
        throw eventsError;
      }

      // Filter for email events
      const emailEvents = events?.filter(event => 
        event.metadata?.type === 'email' || 
        event.metadata?.interaction_type === 'email'
      ) || [];

      setEmailEvents(emailEvents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email events');
    } finally {
      setLoading(false);
    }
  }, [contactId, db]);

  // Check Gmail connection status
  useEffect(() => {
    const checkGmailStatus = async () => {
      const status = await checkConnection();
      setIsGmailConnected(status.isConnected);
    };
    checkGmailStatus();
  }, [checkConnection]);

  // Load email events for this contact
  useEffect(() => {
    loadEmailEvents();
  }, [loadEmailEvents]);

  // Load Gmail emails when contact email is available and Gmail is connected
  useEffect(() => {
    if (contactEmail && isGmailConnected) {
      loadGmailEmails();
    }
  }, [contactEmail, isGmailConnected, loadGmailEmails]);

  const handleComposeEmail = () => {
    if (contactEmail && onComposeEmail) {
      onComposeEmail(contactEmail);
    }
  };

  const handleRefresh = () => {
    loadEmailEvents();
    if (contactEmail && isGmailConnected) {
      loadGmailEmails();
    }
  };

  const handleViewEmail = async (email: Event | EmailMetadata) => {
    setSelectedEmail(email);
    setEmailModalOpen(true);
    setFullEmailContent(null);
    setLoadingEmailContent(false);

    // If it's a Gmail email, fetch the full content
    if ('id' in email && 'snippet' in email) {
      setLoadingEmailContent(true);
      try {
        const fullEmail = await getEmail(email.id);
        if (fullEmail && fullEmail.content) {
          // Prefer HTML content, fallback to text
          const content = fullEmail.content.html || fullEmail.content.text || email.snippet;
          setFullEmailContent(content);
        } else {
          setFullEmailContent(email.snippet);
        }
      } catch (err) {
        console.error('Failed to fetch full email content:', err);
        setFullEmailContent(email.snippet);
      } finally {
        setLoadingEmailContent(false);
      }
    } else {
      // For database events, use the existing content
      const content = email.metadata?.email_snippet || email.description || 'No content available';
      setFullEmailContent(content);
    }
  };

  const handleCloseEmailModal = () => {
    setSelectedEmail(null);
    setEmailModalOpen(false);
    setFullEmailContent(null);
    setLoadingEmailContent(false);
  };

  const handleViewInGmail = () => {
    if (!selectedEmail) return;

    let gmailUrl: string;
    
    if ('gmailUrl' in selectedEmail && selectedEmail.gmailUrl) {
      // Use the Gmail URL from the email metadata
      gmailUrl = selectedEmail.gmailUrl;
    } else if ('threadId' in selectedEmail && selectedEmail.threadId) {
      // Construct Gmail URL from threadId
      gmailUrl = `https://mail.google.com/mail/u/0/#inbox/${selectedEmail.threadId}`;
    } else if ('id' in selectedEmail && selectedEmail.id) {
      // Fallback to using the email ID
      gmailUrl = `https://mail.google.com/mail/u/0/#inbox/${selectedEmail.id}`;
    } else {
      // Last resort - open Gmail inbox
      gmailUrl = 'https://mail.google.com/mail/u/0/#inbox';
    }

    window.open(gmailUrl, '_blank');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEmailDirection = (event: Event) => {
    const metadata = event.metadata;
    if (!metadata) return 'unknown';

    // Check if this is an outgoing email (sent by user)
    if (metadata.email_from && metadata.email_to) {
      // This is a simplified check - in a real app you'd compare with user's email
      return 'sent';
    }
    
    return 'received';
  };

  const getEmailStatusColor = (event: Event) => {
    const status = event.metadata?.status;
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };


  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email History
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email History
        </Typography>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Email History
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          {contactEmail && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleComposeEmail}
            >
              Compose Email
            </Button>
          )}
        </Box>
      </Box>

      {!contactEmail ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Add Email Address
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add an email address to this contact to view email history
          </Typography>
        </Box>
      ) : emailEvents.length === 0 && gmailEmails.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No email history
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isGmailConnected 
              ? `No emails found for ${contactName || contactEmail}`
              : 'Connect Gmail to view email history'
            }
          </Typography>
          {isGmailConnected && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleComposeEmail}
              sx={{ mt: 2 }}
            >
              Send Email
            </Button>
          )}
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {emailEvents.length + gmailEmails.length} email{(emailEvents.length + gmailEmails.length) !== 1 ? 's' : ''} found
            {emailEvents.length > 0 && gmailEmails.length > 0 && (
              <span> ({emailEvents.length} from events, {gmailEmails.length} from Gmail)</span>
            )}
          </Typography>

          <List>
            {emailEvents.map((event, index) => (
              <React.Fragment key={event.id}>
                <ListItem
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: getEmailDirection(event) === 'sent' ? 'success.main' : 'primary.main',
                        width: 40,
                        height: 40
                      }}
                    >
                      <EmailIcon />
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, flexGrow: 1 }}
                        >
                          {event.metadata?.email_subject || event.title}
                        </Typography>
                        
                        <Chip
                          label={event.metadata?.status || 'unknown'}
                          size="small"
                          color={getEmailStatusColor(event)}
                          variant="outlined"
                        />
                        
                        {getEmailDirection(event) === 'sent' ? (
                          <Chip label="Sent" size="small" color="success" />
                        ) : (
                          <Chip label="Received" size="small" color="primary" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {getEmailDirection(event) === 'sent' ? 'To' : 'From'}:{' '}
                          {getEmailDirection(event) === 'sent' 
                            ? event.metadata?.email_to 
                            : event.metadata?.email_from
                          }
                        </Typography>
                        
                        {event.metadata?.email_snippet && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {event.metadata.email_snippet}
                          </Typography>
                        )}
                        
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(event.created_at)}
                        </Typography>
                      </Box>
                    }
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewEmail(event)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {getEmailDirection(event) === 'received' && (
                      <Tooltip title="Reply">
                        <IconButton size="small">
                          <ReplyIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItem>
                {(index < emailEvents.length - 1 || gmailEmails.length > 0) && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
            
            {gmailEmails.map((email, index) => (
              <React.Fragment key={email.id}>
                <ListItem
                  sx={{
                    py: 2,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: email.labels?.includes('SENT') ? 'success.main' : 'primary.main',
                        width: 40,
                        height: 40
                      }}
                    >
                      <EmailIcon />
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, flexGrow: 1 }}
                        >
                          {email.subject || 'No Subject'}
                        </Typography>
                        
                        {email.labels?.includes('SENT') ? (
                          <Chip label="Sent" size="small" color="success" />
                        ) : (
                          <Chip label="Received" size="small" color="primary" />
                        )}
                        
                        <Chip label="Gmail" size="small" variant="outlined" />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {email.labels?.includes('SENT') ? 'To' : 'From'}:{' '}
                          {email.labels?.includes('SENT') ? email.to : email.from}
                        </Typography>
                        
                        {email.snippet && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {email.snippet}
                          </Typography>
                        )}
                        
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(email.date)}
                        </Typography>
                      </Box>
                    }
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewEmail(email)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {!email.labels?.includes('SENT') && (
                      <Tooltip title="Reply">
                        <IconButton size="small">
                          <ReplyIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </ListItem>
                {index < gmailEmails.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        </>
      )}

      {gmailError && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Gmail integration error: {gmailError}
        </Alert>
      )}

      {/* Email Details Modal */}
      <Dialog
        open={emailModalOpen}
        onClose={handleCloseEmailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedEmail && 'subject' in selectedEmail 
                ? selectedEmail.subject || 'No Subject'
                : selectedEmail && 'metadata' in selectedEmail && selectedEmail.metadata?.email_subject 
                ? selectedEmail.metadata.email_subject 
                : 'Email Details'}
            </Typography>
            <IconButton onClick={handleCloseEmailModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Box>
              {/* Email metadata */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  From
                </Typography>
                <Typography variant="body1">
                  {selectedEmail && 'from' in selectedEmail 
                    ? selectedEmail.from
                    : selectedEmail && 'metadata' in selectedEmail && selectedEmail.metadata?.email_from
                    ? selectedEmail.metadata.email_from
                    : 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  To
                </Typography>
                <Typography variant="body1">
                  {selectedEmail && 'to' in selectedEmail 
                    ? Array.isArray(selectedEmail.to) ? selectedEmail.to.join(', ') : selectedEmail.to
                    : selectedEmail && 'metadata' in selectedEmail && selectedEmail.metadata?.email_to
                    ? selectedEmail.metadata.email_to
                    : 'Unknown'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Date
                </Typography>
                <Typography variant="body1">
                  {selectedEmail && 'date' in selectedEmail 
                    ? formatDate(selectedEmail.date)
                    : selectedEmail && 'created_at' in selectedEmail
                    ? formatDate(selectedEmail.created_at)
                    : 'Unknown'}
                </Typography>
              </Box>

              {/* Email content */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Message
              </Typography>
              <Box sx={{ 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 2, 
                backgroundColor: 'grey.50',
                minHeight: 200,
                overflow: 'auto'
              }}>
                {loadingEmailContent ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                      Loading email content...
                    </Typography>
                  </Box>
                ) : fullEmailContent ? (
                  // Check if content is HTML
                  fullEmailContent.includes('<') && fullEmailContent.includes('>') ? (
                    <Box 
                      sx={{ 
                        '& p': { margin: '0 0 8px 0' },
                        '& br': { lineHeight: '1.5' },
                        color: 'black'
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: (() => {
                          const textarea = document.createElement('textarea');
                          textarea.innerHTML = fullEmailContent;
                          return textarea.value;
                        })()
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'black' }}>
                      {(() => {
                        const textarea = document.createElement('textarea');
                        textarea.innerHTML = fullEmailContent;
                        return textarea.value;
                      })()}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No content available
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleViewInGmail}
            startIcon={<LaunchIcon />}
            variant="outlined"
          >
            View in Gmail
          </Button>
          <Button onClick={handleCloseEmailModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
